import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { firstValueFrom } from 'rxjs';

import { APP_PATHS } from '../routing/app-paths';
import { CustomerSessionService } from './customer-session.service';

/**
 * Gate of the `/panel` branch. It asks the backend, never the URL or the e-mail
 * address: on a cold start (status `unknown`, e.g. after a reload) it awaits the
 * single-flight silent refresh and lets the HTTP verdict decide.
 *
 * Mounted TWICE on that branch and deliberately as ONE function (Q-188/A): as
 * `canActivate`, which runs on entry, and as `canActivateChild`, which runs on every
 * navigation BETWEEN panel sections. Angular's `CanActivateFn` and `CanActivateChildFn`
 * have the same shape, so this is the same rule in one place rather than two — and it
 * is what makes „a dead session in a live panel" stop being a reachable state instead
 * of a thing patched screen by screen.
 *
 * Two runs of one navigation cost ONE request, not two: `refresh()` is only called at
 * status `unknown`, and the first run settles it (at `anonymous` the guard asks nothing
 * at all — see below).
 *
 * It deliberately does NOT branch on that verdict. Access to the panel is a binary
 * property and only a confirmed session opens it: `refresh()` answering
 * `'throttled'` (the backend's rate limit on `/refresh`) is not a session, so it
 * redirects exactly like a rejection. Carrying that distinction to the login screen
 * is the session service's job — the screen reads `isRefreshThrottled`, the guard
 * does not repeat it. If a special case for the throttle ever looks necessary here,
 * the suppression inside `CustomerSessionService.refresh()` is what is broken.
 *
 * At status `anonymous` the guard still does not ask the backend at all; that is
 * pre-existing behaviour, left unchanged on purpose.
 *
 * A denied navigation keeps the target in `returnUrl` so the login screen can send
 * the customer back where they were heading (`resolveReturnUrl` validates it on
 * the way out — the value travels through a query string the user controls).
 */
export const customerAuthGuard: CanActivateFn = async (_route, state) => {
  const session = inject(CustomerSessionService);
  const router = inject(Router);

  if (session.status() === 'unknown') {
    await firstValueFrom(session.refresh());
  }

  if (session.isAuthenticated()) {
    return true;
  }

  return router.createUrlTree([APP_PATHS.login], { queryParams: { returnUrl: state.url } });
};
